import { fetchDetailPermissionsQueryOptions } from "@/api/permissionsQueryOptions";
import { getOrganization } from "@/store/auth/authSlice";
import MemberAwardIcon from "@/ui/icons/member/MemberAwardIcon";
import MemberEducationIcon from "@/ui/icons/member/MemberEducationIcon";
import MemberSectionsIcon from "@/ui/icons/member/MemberSectionsIcon";
import MembersIcon from "@/ui/icons/MembersIcon";
import AddCircleIcon from '@mui/icons-material/AddCircle';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutlined';
import BarChartIcon from '@mui/icons-material/BarChart';
import BusinessIcon from '@mui/icons-material/Business';
import CakeIcon from '@mui/icons-material/Cake';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CorporateFareIcon from '@mui/icons-material/CorporateFare';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import HowToRegIcon from '@mui/icons-material/HowToReg';
import ImportExportIcon from '@mui/icons-material/ImportExport';
import ListIcon from '@mui/icons-material/List';
import LoginIcon from '@mui/icons-material/Login';
import MarkAsUnreadIcon from '@mui/icons-material/MarkAsUnread';
import ReorderIcon from '@mui/icons-material/Reorder';
import SentimentSatisfiedAltIcon from '@mui/icons-material/SentimentSatisfiedAlt';
import SpellcheckIcon from '@mui/icons-material/Spellcheck';
import SummarizeIcon from '@mui/icons-material/Summarize';
import Divider from "@mui/material/Divider";
import List from "@mui/material/List";
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import { useSuspenseQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useFlag } from '@unleash/proxy-client-react';
import i18next, { t } from "i18next";
import { useAuth } from "react-oidc-context";
import { useSelector } from "react-redux";
import useResponsive from "./hooks/useResponsive";

export type MainLinksProps = {
    toggleDrawer?: () => void
}

export default function MainLinks(props: MainLinksProps) {

    const auth = useAuth()
    const sessionOrganization = useSelector(getOrganization)

    const permissionsQuery = useSuspenseQuery(fetchDetailPermissionsQueryOptions('organization', sessionOrganization.uuid, ['AwardOverview', 'EducationOverview', 'RegistrationSnapshot', 'GrinsRecord', 'MedusaImport', 'Member', 'AddressDataQuality', 'MemberDataQuality', 'ListHeader', 'MemberSectionBulkUpdate', 'Charts', 'PostreceiverOverview', 'OrganizationOverview', 'PfadfinderReports', 'GildeReports', 'scoutcardView', 'BirthdayList']))
    const permissions = permissionsQuery.data

    const readAwards = permissions && permissions.filter(p => p.detail === 'AwardOverview' && p.permissions.some(a => a === 'read')).length > 0
    const readEducations = permissions && permissions.filter(p => p.detail === 'EducationOverview' && p.permissions.some(a => a === 'read')).length > 0
    const readRegistration = permissions && permissions.filter(p => p.detail === 'RegistrationSnapshot' && p.permissions.some(a => a === 'read')).length > 0
    const readAddressDataQuality = permissions && permissions.filter(p => p.detail === 'AddressDataQuality' && p.permissions.some(a => a === 'read')).length > 0
    const readMemberDataQuality = permissions && permissions.filter(p => p.detail === 'MemberDataQuality' && p.permissions.some(a => a === 'read')).length > 0
    const readPostreceiver = permissions && permissions.filter(p => p.detail === 'PostreceiverOverview' && p.permissions.some(a => a === 'read')).length > 0
    const readScoutcard = permissions && permissions.filter(p => p.detail === 'scoutcardView' && p.permissions.some(a => a === 'read')).length > 0
    const readOrganizationHierarchy = permissions && permissions.filter(p => p.detail === 'OrganizationOverview' && p.permissions.some(a => a === 'read')).length > 0
    const readCharts = permissions && permissions.filter(p => p.detail === 'Charts' && p.permissions.some(a => a === 'read')).length > 0
    const readList = permissions && permissions.filter(p => p.detail === 'ListHeader' && p.permissions.some(a => a === 'read')).length > 0
    const readBirthdayList = permissions && permissions.filter(p => p.detail === 'BirthdayList' && p.permissions.some(a => a === 'read')).length > 0

    const readGrins = permissions && permissions.filter(p => p.detail === 'GrinsRecord' && p.permissions.some(a => a === 'read')).length > 0
    const medusaImport = permissions && permissions.filter(p => p.detail === 'MedusaImport' && p.permissions.some(a => a === 'write')).length > 0

    const readPfadfinderReports = permissions && permissions.filter(p => p.detail === 'PfadfinderReports' && p.permissions.some(a => a === 'read')).length > 0
    const readGildeReports = permissions && permissions.filter(p => p.detail === 'GildeReports' && p.permissions.some(a => a === 'read')).length > 0

    const createMember = permissions && permissions.filter(p => p.detail === 'Member' && p.permissions.some(a => a === 'create')).length > 0
    const updateMemberSectionBulk = permissions && permissions.filter(p => p.detail === 'MemberSectionBulkUpdate' && p.permissions.some(a => a === 'write')).length > 0

    const listsFlag = useFlag('lists');
    const sectionBulkChangeFlag = useFlag('section_bulk_change');
    const createMultipleMemberFlag = useFlag('create_multiple_member');
    const organizationOverview = useFlag('organization_overview');
    const reports = useFlag('reports');
    const debug_info = useFlag('debug_info');

    const responsive = useResponsive();
    const storage = sessionStorage

    const show_debug_info = import.meta.env.VITE_DEBUG === 'true' || debug_info

    return (
        <>
            {
                auth.isAuthenticated && storage.getItem('AccessKey') &&
                <>
                    <List component="nav" disablePadding>
                        <Link
                            to={'/membershipsOverview'}
                            search={{
                                filter: [
                                    { field: "active", operator: "is", value: "true" },
                                    { field: "organization.uuid", operator: "isAnyOf", value: [sessionOrganization.uuid] },
                                    { field: "scoutstate", operator: "isAnyOf", value: ['MEMBER_FULL', 'MEMBER_ADD'] }
                                ],
                                sort: [{ field: "member.scoutId", direction: "asc" }],
                                visibility: [{ field: "properties.joblist" }, { field: "properties.section" }],
                            }}
                            activeOptions={{
                                includeSearch: false
                            }}
                            onClick={() => {
                                if (props.toggleDrawer) {
                                    props.toggleDrawer()
                                }
                            }}
                        >
                            <ListItemButton sx={{
                                pl: 2,
                                "&.MuiButtonBase-root:hover": {
                                    bgcolor: "transparent"
                                }
                            }}>
                                <ListItemIcon sx={{ minWidth: '30px' }}>
                                    <MembersIcon />
                                </ListItemIcon>
                                <ListItemText primary={t('menu.members.group')} disableTypography>

                                </ListItemText>
                            </ListItemButton>
                        </Link>

                        <List component="div" disablePadding>
                            <Link
                                to={'/membershipsOverview'}
                                search={{
                                    filter: [
                                        { field: "active", operator: "is", value: "true" },
                                        { field: "organization.uuid", operator: "isAnyOf", value: [sessionOrganization.uuid] },
                                        { field: "scoutstate", operator: "isAnyOf", value: ['MEMBER_FULL', 'MEMBER_ADD'] }
                                    ],
                                    sort: [{ field: "member.scoutId", direction: "asc" }],
                                    visibility: [{ field: "properties.joblist" }, { field: "properties.section" }],
                                }}
                                activeProps={{
                                    className: 'scoreg-active-button',
                                }}
                                activeOptions={{
                                    includeSearch: false
                                }}
                                onClick={() => {
                                    if (props.toggleDrawer) {
                                        props.toggleDrawer()
                                    }
                                }}
                            >
                                <ListItemButton sx={{ pl: 4 }}>
                                    <ListItemIcon sx={{ minWidth: '30px' }}>
                                        <FormatListNumberedIcon />
                                    </ListItemIcon>
                                    <ListItemText primary={t('menu.members.overview')} />
                                </ListItemButton>
                            </Link>
                            {readBirthdayList &&
                                <Link
                                    to={'/birthdays'}
                                    activeProps={{
                                        className: 'scoreg-active-button',
                                    }}
                                    activeOptions={{
                                        includeSearch: false
                                    }}
                                    onClick={() => {
                                        if (props.toggleDrawer) {
                                            props.toggleDrawer()
                                        }
                                    }}
                                >
                                    <ListItemButton sx={{ pl: 4 }}>
                                        <ListItemIcon sx={{ minWidth: '30px' }}>
                                            <CakeIcon />
                                        </ListItemIcon>
                                        <ListItemText primary={t('menu.members.birthdays')} />
                                    </ListItemButton>
                                </Link>
                            }
                            {createMember &&
                                <Link
                                    to={'/createMember'}
                                    activeProps={{
                                        className: 'scoreg-active-button',
                                    }}
                                    activeOptions={{
                                        includeSearch: false
                                    }}
                                    onClick={() => {
                                        if (props.toggleDrawer) {
                                            props.toggleDrawer()
                                        }
                                    }}
                                >
                                    <ListItemButton sx={{ pl: 4 }}>
                                        <ListItemIcon sx={{ minWidth: '30px' }}>
                                            <AddCircleIcon />
                                        </ListItemIcon>
                                        <ListItemText primary={t('menu.members.createSingle')} />
                                    </ListItemButton>
                                </Link>
                            }
                            {createMember && createMultipleMemberFlag && <>
                                <Link
                                    to={'/uploadMember'}
                                    activeProps={{
                                        className: 'scoreg-active-button',
                                    }}
                                    activeOptions={{
                                        includeSearch: false
                                    }}
                                    onClick={() => {
                                        if (props.toggleDrawer) {
                                            props.toggleDrawer()
                                        }
                                    }}
                                >
                                    <ListItemButton sx={{ pl: 4 }}>
                                        <ListItemIcon sx={{ minWidth: '30px' }}>
                                            <AddCircleOutlineIcon />
                                        </ListItemIcon>
                                        <ListItemText primary={t('menu.members.createMultiple')} />
                                    </ListItemButton>
                                </Link>
                            </>
                            }
                            {readList && listsFlag &&
                                <Link
                                    to={'/lists'}
                                    activeProps={{
                                        className: 'scoreg-active-button',
                                    }}
                                    activeOptions={{
                                        includeSearch: false
                                    }}
                                    onClick={() => {
                                        if (props.toggleDrawer) {
                                            props.toggleDrawer()
                                        }
                                    }}
                                >
                                    <ListItemButton sx={{ pl: 4 }}>
                                        <ListItemIcon sx={{ minWidth: '30px' }}>
                                            <ListIcon />
                                        </ListItemIcon>
                                        <ListItemText primary={t('menu.lists')} />
                                    </ListItemButton>
                                </Link>
                            }
                            {readList && listsFlag &&
                                <Link
                                    to={'/calendar'}
                                    activeProps={{
                                        className: 'scoreg-active-button',
                                    }}
                                    activeOptions={{
                                        includeSearch: false
                                    }}
                                    onClick={() => {
                                        if (props.toggleDrawer) {
                                            props.toggleDrawer()
                                        }
                                    }}
                                >
                                    <ListItemButton sx={{ pl: 4 }}>
                                        <ListItemIcon sx={{ minWidth: '30px' }}>
                                            <CalendarMonthIcon />
                                        </ListItemIcon>
                                        <ListItemText primary={t('menu.calendar')} />
                                    </ListItemButton>
                                </Link>
                            }
                            {updateMemberSectionBulk && sectionBulkChangeFlag &&
                                <Link
                                    to={'/bulkUpdateSection'}
                                    activeProps={{
                                        className: 'scoreg-active-button',
                                    }}
                                    activeOptions={{
                                        includeSearch: false
                                    }}
                                    onClick={() => {
                                        if (props.toggleDrawer) {
                                            props.toggleDrawer()
                                        }
                                    }}
                                >
                                    <ListItemButton sx={{ pl: 4 }}>
                                        <ListItemIcon sx={{ minWidth: '30px' }}>
                                            <MemberSectionsIcon />
                                        </ListItemIcon>
                                        <ListItemText primary={t('menu.bulkChangeSection')} />
                                    </ListItemButton>
                                </Link>
                            }
                        </List>

                        <Divider />

                        {readEducations &&
                            <Link
                                to={'/education'}
                                activeProps={{
                                    className: 'scoreg-active-button',
                                }}
                                activeOptions={{
                                    includeSearch: false
                                }}
                                onClick={() => {
                                    if (props.toggleDrawer) {
                                        props.toggleDrawer()
                                    }
                                }}
                            >
                                <ListItemButton>
                                    <ListItemIcon sx={{ minWidth: '30px' }}>
                                        <MemberEducationIcon />
                                    </ListItemIcon>
                                    <ListItemText primary={t('menu.education')} />
                                </ListItemButton>
                            </Link>
                        }
                        {readAwards &&
                            <Link
                                to={'/awards'}
                                activeProps={{
                                    className: 'scoreg-active-button',
                                }}
                                activeOptions={{
                                    includeSearch: false
                                }}
                                onClick={() => {
                                    if (props.toggleDrawer) {
                                        props.toggleDrawer()
                                    }
                                }}
                            >
                                <ListItemButton>
                                    <ListItemIcon sx={{ minWidth: '30px' }}>
                                        <MemberAwardIcon />
                                    </ListItemIcon>
                                    <ListItemText primary={t('menu.awards')} />
                                </ListItemButton>
                            </Link>
                        }
                        {readRegistration &&
                            <Link
                                to={'/registration'}
                                activeProps={{
                                    className: 'scoreg-active-button',
                                }}
                                activeOptions={{
                                    includeSearch: false
                                }}
                                onClick={() => {
                                    if (props.toggleDrawer) {
                                        props.toggleDrawer()
                                    }
                                }}
                            >
                                <ListItemButton>
                                    <ListItemIcon sx={{ minWidth: '30px' }}>
                                        <HowToRegIcon />
                                    </ListItemIcon>
                                    <ListItemText primary={t('menu.registration')} />
                                </ListItemButton>
                            </Link>
                        }
                        {readGrins &&
                            <>
                                <Link
                                    to={'/grinsView'}
                                    onClick={() => {
                                        if (props.toggleDrawer) {
                                            props.toggleDrawer()
                                        }
                                    }}
                                >
                                    <ListItemButton>
                                        <ListItemIcon sx={{ minWidth: '30px' }}>
                                            <SentimentSatisfiedAltIcon />
                                        </ListItemIcon>
                                        <ListItemText primary={t('menu.grins')} />
                                    </ListItemButton>
                                </Link>
                                <Link
                                    to={'/grinsView'}
                                    activeProps={{
                                        className: 'scoreg-active-button',
                                    }}
                                    activeOptions={{
                                        includeSearch: false
                                    }}
                                    onClick={() => {
                                        if (props.toggleDrawer) {
                                            props.toggleDrawer()
                                        }
                                    }}
                                >
                                    <ListItemButton sx={{ pl: 4 }}>
                                        <ListItemIcon sx={{ minWidth: '30px' }}>
                                            <ReorderIcon />
                                        </ListItemIcon>
                                        <ListItemText primary={t('menu.grinsView')} />
                                    </ListItemButton>
                                </Link>
                                <Link
                                    to={'/grinsExports'}
                                    activeProps={{
                                        className: 'scoreg-active-button',
                                    }}
                                    activeOptions={{
                                        includeSearch: false
                                    }}
                                    onClick={() => {
                                        if (props.toggleDrawer) {
                                            props.toggleDrawer()
                                        }
                                    }}
                                >

                                    <ListItemButton sx={{ pl: 4 }}>
                                        <ListItemIcon sx={{ minWidth: '30px' }}>
                                            <ImportExportIcon />
                                        </ListItemIcon>
                                        <ListItemText primary={t('menu.grinsExports')} />
                                    </ListItemButton>
                                </Link>
                            </>
                        }
                        {medusaImport &&
                            <Link
                                to={'/medusaImport'}
                                activeProps={{
                                    className: 'scoreg-active-button',
                                }}
                                activeOptions={{
                                    includeSearch: false
                                }}
                                onClick={() => {
                                    if (props.toggleDrawer) {
                                        props.toggleDrawer()
                                    }
                                }}
                            >

                                <ListItemButton>
                                    <ListItemIcon sx={{ minWidth: '30px' }}>
                                        <CloudUploadIcon />
                                    </ListItemIcon>
                                    <ListItemText primary={t('menu.medusaImport')} />
                                </ListItemButton>
                            </Link>
                        }
                        {readCharts &&
                            <Link
                                to={'/charts'}
                                activeProps={{
                                    className: 'scoreg-active-button',
                                }}
                                activeOptions={{
                                    includeSearch: false
                                }}
                                onClick={() => {
                                    if (props.toggleDrawer) {
                                        props.toggleDrawer()
                                    }
                                }}
                            >
                                <ListItemButton>
                                    <ListItemIcon sx={{ minWidth: '30px' }}>
                                        <BarChartIcon />
                                    </ListItemIcon>
                                    <ListItemText primary={t('menu.charts')} />
                                </ListItemButton>
                            </Link>
                        }
                        {readPostreceiver &&
                            <>
                                <Divider />
                                <Link
                                    to={'/postreceiver'}
                                    activeProps={{
                                        className: 'scoreg-active-button',
                                    }}
                                    onClick={() => {
                                        if (props.toggleDrawer) {
                                            props.toggleDrawer()
                                        }
                                    }}
                                >
                                    <ListItemButton>
                                        <ListItemIcon sx={{ minWidth: '30px' }}>
                                            <MarkAsUnreadIcon />
                                        </ListItemIcon>
                                        <ListItemText primary={t('menu.postreceiver')} />
                                    </ListItemButton>
                                </Link>
                            </>
                        }
                        {readScoutcard &&
                            <>
                                <Link
                                    to={'/scoutcards'}
                                    activeProps={{
                                        className: 'scoreg-active-button',
                                    }}
                                    onClick={() => {
                                        if (props.toggleDrawer) {
                                            props.toggleDrawer()
                                        }
                                    }}
                                >
                                    <ListItemButton>
                                        <ListItemIcon sx={{ minWidth: '30px' }}>
                                            <CreditCardIcon />
                                        </ListItemIcon>
                                        <ListItemText primary={t('menu.scoutcard')} />
                                    </ListItemButton>
                                </Link>
                                <List component="div" disablePadding>
                                    <Link
                                        to={'/scoutcardRequests'}
                                        activeProps={{
                                            className: 'scoreg-active-button',
                                        }}
                                        onClick={() => {
                                            if (props.toggleDrawer) {
                                                props.toggleDrawer()
                                            }
                                        }}
                                    >
                                        <ListItemButton sx={{ pl: 4 }}>
                                            <ListItemIcon sx={{ minWidth: '30px' }}>
                                                <CreditCardIcon />
                                            </ListItemIcon>
                                            <ListItemText primary={t('menu.scoutcardRequests')} />
                                        </ListItemButton>
                                    </Link>
                                </List>
                            </>
                        }
                        {readOrganizationHierarchy && organizationOverview &&
                            <>
                                <Divider />
                                <Link
                                    to={'/organizationHierarchy'}
                                    activeProps={{
                                        className: 'scoreg-active-button',
                                    }}
                                    onClick={() => {
                                        if (props.toggleDrawer) {
                                            props.toggleDrawer()
                                        }
                                    }}
                                >
                                    <ListItemButton>
                                        <ListItemIcon sx={{ minWidth: '30px' }}>
                                            <CorporateFareIcon />
                                        </ListItemIcon>
                                        <ListItemText primary={t('menu.organizations')} />
                                    </ListItemButton>
                                </Link>
                            </>
                        }



                        <Divider />

                        {(readAddressDataQuality || readMemberDataQuality) &&
                            <>

                                <ListItemButton style={{ color: 'rgb(85, 85, 85)' }}>
                                    <ListItemIcon sx={{ minWidth: '30px' }}>
                                        <SpellcheckIcon />
                                    </ListItemIcon>
                                    <ListItemText primary={t('menu.quality.group')} disableTypography>

                                    </ListItemText>
                                </ListItemButton>

                                <List component="div" disablePadding>
                                    {readMemberDataQuality &&
                                        <Link
                                            to={'/qualityMember'}
                                            activeProps={{
                                                className: 'scoreg-active-button',
                                            }}
                                            activeOptions={{
                                                includeSearch: false
                                            }}
                                            onClick={() => {
                                                if (props.toggleDrawer) {
                                                    props.toggleDrawer()
                                                }
                                            }}
                                        >
                                            <ListItemButton sx={{ pl: 4 }}>
                                                <ListItemIcon sx={{ minWidth: '30px' }}>
                                                    <MembersIcon />
                                                </ListItemIcon>
                                                <ListItemText primary={t('menu.quality.member')} />
                                            </ListItemButton>
                                        </Link>
                                    }
                                    {readAddressDataQuality &&
                                        <Link
                                            to={'/qualityAddress'}
                                            activeProps={{
                                                className: 'scoreg-active-button',
                                            }}
                                            activeOptions={{
                                                includeSearch: false
                                            }}
                                            onClick={() => {
                                                if (props.toggleDrawer) {
                                                    props.toggleDrawer()
                                                }
                                            }}
                                        >
                                            <ListItemButton sx={{ pl: 4 }}>
                                                <ListItemIcon sx={{ minWidth: '30px' }}>
                                                    <BusinessIcon />
                                                </ListItemIcon>
                                                <ListItemText primary={t('menu.quality.address')} />
                                            </ListItemButton>
                                        </Link>
                                    }
                                </List>
                                <Divider />
                            </>
                        }

                        {readGildeReports && reports &&
                            <>
                                <Link
                                    to={'/gildeReports'}
                                    activeProps={{
                                        className: 'scoreg-active-button',
                                    }}
                                    activeOptions={{
                                        includeSearch: false
                                    }}
                                    onClick={() => {
                                        if (props.toggleDrawer) {
                                            props.toggleDrawer()
                                        }
                                    }}
                                >
                                    <ListItemButton>
                                        <ListItemIcon sx={{ minWidth: '30px' }}>
                                            <SummarizeIcon />
                                        </ListItemIcon>
                                        <ListItemText primary={t('menu.gildeReports')} />
                                    </ListItemButton>
                                </Link>
                                <Divider />
                            </>
                        }
                        {readPfadfinderReports && reports &&
                            <>
                                <Link
                                    to={'/reports'}
                                    activeProps={{
                                        className: 'scoreg-active-button',
                                    }}
                                    onClick={() => {
                                        if (props.toggleDrawer) {
                                            props.toggleDrawer()
                                        }
                                    }}
                                >
                                    <ListItemButton>
                                        <ListItemIcon sx={{ minWidth: '30px' }}>
                                            <SummarizeIcon />
                                        </ListItemIcon>
                                        <ListItemText primary={t('menu.reports')} />
                                    </ListItemButton>
                                </Link>
                                <Divider />
                            </>
                        }

                    </List>

                </>
            }

            {
                !auth.isAuthenticated &&
                <ListItemButton onClick={() => {
                    auth.signinRedirect();
                }
                }>
                    <ListItemIcon sx={{ minWidth: '30px' }}>
                        <LoginIcon />
                    </ListItemIcon>
                    <ListItemText primary={t('action.login')} />
                </ListItemButton>
            }

            {auth.isAuthenticated && storage.getItem('AccessKey') && show_debug_info && <>
                <p>{i18next.resolvedLanguage}</p>
                <p>{responsive.xsmall && 'xsmall'}</p>
                <p>{responsive.small && 'small'}</p>
                <p>{responsive.medium && 'medium'}</p>
                <p>{responsive.large && 'large'}</p>
                <p>{responsive.xlarge && 'xlarge'}</p>
            </>}
        </>
    )
}